import {NucleotideType} from 'biojs/BioConstants';
import {
  BaseGene,
  BaseTranscript,
  Chromosome,
  Gene,
  Genome,
  GenomeOption,
  Transcript,
} from 'constants/GenomeConstants';
import {SequenceTopology} from 'constants/SequenceConstants';
import {StudySearchItem} from 'studies/StudySearchInput';

export interface SequenceImportOptions {
  folderId?: string;
  nucleotideType: NucleotideType;
  tagSchemaId?: string;
  topology: SequenceTopology;
  studySearchItems?: Array<StudySearchItem>;
}

// ---- Import from DB

export interface DBSequence {
  name: string;
  acc: string;
}

export interface DBSequenceVersion {
  genome: Genome;
  chromosome: Chromosome;
  gene: Gene;
  transcripts: Array<Transcript>;
}

// Keep in sync with the handles in benchling/pybio/seqio/connectors/
export enum DBHandle {
  PRIVATE = 'private',
  ENSEMBL = 'ensembl',
  ADDGENE = 'addgene',
  JBEIR = 'jbeir',
  NCBI = 'ncbi',
  PARTS_REGISTRY = 'partsregistry',
}

// The types in this section are based on the returned and expected formats depending on where they're sourced,
// per the various importers in benchling/pybio/seqio/connectors/
// These can be generally categorized as Ensembl-based sequences (live or privately stored)
// and non-Ensembl sequences, each of which offers slightly different import options and types of data.

export interface DBImportItemOptions {
  accession: string | EnsemblDBImportAccession;
  name: string;
  asCDNA?: boolean;
  revcomp?: boolean;
  selectedVersionIndex?: number;
  isSelected: boolean;
}

export interface EnsemblDBImportAccession {
  upstream: number;
  downstream: number;
  transcriptIds: Array<string>;
}

// As required by the /dbimport endpoint
export interface EnsemblDBImportAccessionData extends Omit<EnsemblDBImportAccession, 'transcriptIds'> {
  transcripts: Array<string>;
  gene: string | Gene | BaseGene;
  chromosomeName: string | number;
  speciesName: string;
}

export interface LiveEnsemblTranscript extends BaseTranscript {
  id?: string;
  biotype: string;
  start: number;
  end: number;
  isCanonical?: boolean;
}

export interface LiveEnsemblGenome extends Omit<Genome, 'id'> {
  id?: number;
  ensemblVersion?: number;
}

export interface DBQueryResultVersion {
  chromosome: Omit<Chromosome, 'length'> | Chromosome;
  gene: Gene | BaseGene;
  genome: Genome | LiveEnsemblGenome;
  transcripts: Array<Transcript | LiveEnsemblTranscript>;
}

interface DBQueryResultOptionalData {
  description?: string;
  name: string;
  length?: number;
  type?: string;
  sequences?: Array<DBSequence>;
  versions?: Array<DBQueryResultVersion>;
}

export interface DBQueryResult extends DBQueryResultOptionalData {
  dbHandle: DBHandle;
  dbImg: string | null;
  dbName: string;
  acc: string;
  accLink?: string;
}

export interface DBQueryResponse extends DBQueryResultOptionalData {
  _dbhandle: DBHandle;
  _dbname: string;
  _dbimg: string | null;
  acc: string;
  _link?: string;
  _sequences?: Array<DBSequence>;
}

export interface DBImportSequence {
  resultData: DBQueryResult;
  importOptions: DBImportItemOptions;
}

// ---- Import chromosomal regions

export interface ChromosomalImportData {
  chromosomeCoords: Array<ChromosomalCoordInput>;
  genome?: GenomeOption;
}

export interface ChromosomalCoordInput {
  chromosome: Chromosome | null;
  start: number | null;
  end: number | null;
  cdsCoords: string;
}

export interface CDSCoordinates {
  start: number;
  end: number;
  displayCoords: boolean;
}

export interface ChromosomalCoords {
  name: string;
  start: number;
  end: number;
  cdsCoords: Array<CDSCoordinates>;
}

// ---- Create new sequence

export interface RawImportData {
  name: string;
  bases: string;
}
